import { useState, useEffect } from 'react';
import { useAdminCrud } from '@/hooks/useAdminCrud';
import { AdminHeader, LoadingGrid, EmptyState, ItemCard, FormActions } from '@/components/admin/AdminComponents';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { HardDrive } from 'lucide-react';

const initialForm = {
  model_name: '', short_description: '', main_image: '', key_features: '',
  channel_count: '', use_cases: '', datasheet_url: '', status: 'Active'
};

const AdminMDVR = () => {
  const { items, loading, isDialogOpen, editingItem, setIsDialogOpen, createItem, updateItem, deleteItem, openCreate, openEdit, closeDialog } = useAdminCrud('mdvr-recorders', 'MDVR recorder');
  const [formData, setFormData] = useState(initialForm);

  useEffect(() => {
    if (editingItem) {
      setFormData({
        model_name: editingItem.model_name, short_description: editingItem.short_description,
        main_image: editingItem.main_image || '', key_features: editingItem.key_features || '',
        channel_count: editingItem.channel_count || '', use_cases: editingItem.use_cases || '',
        datasheet_url: editingItem.datasheet_url || '', status: editingItem.status
      });
    } else {
      setFormData(initialForm);
    }
  }, [editingItem]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const success = editingItem ? await updateItem(editingItem.id, formData) : await createItem(formData);
    if (success) setFormData(initialForm);
  };

  const update = (field) => (e) => setFormData(prev => ({ ...prev, [field]: e.target.value }));
  const updateSelect = (field) => (v) => setFormData(prev => ({ ...prev, [field]: v }));

  return (
    <div data-testid="admin-mdvr">
      <AdminHeader
        title="MDVR & AI Recorders" subtitle="Manage MDVR recorder products" addLabel="Add Recorder"
        isDialogOpen={isDialogOpen} onOpenChange={(open) => { setIsDialogOpen(open); if (!open) { closeDialog(); setFormData(initialForm); } }}
        dialogTitle={editingItem ? 'Edit MDVR Recorder' : 'Add MDVR Recorder'}
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Model Name *</Label>
              <Input value={formData.model_name} onChange={update('model_name')} required placeholder="e.g., DVR-4CH AI" />
            </div>
            <div className="space-y-2">
              <Label>Channel Count</Label>
              <Input value={formData.channel_count} onChange={update('channel_count')} placeholder="e.g., 4 Channels" />
            </div>
          </div>
          <div className="space-y-2">
            <Label>Short Description *</Label>
            <Textarea value={formData.short_description} onChange={update('short_description')} required rows={2} />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Main Image URL</Label>
              <Input value={formData.main_image} onChange={update('main_image')} placeholder="https://..." />
            </div>
            <div className="space-y-2">
              <Label>Status</Label>
              <Select value={formData.status} onValueChange={updateSelect('status')}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Active">Active</SelectItem>
                  <SelectItem value="Legacy">Legacy</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-2">
            <Label>Key Features (one per line)</Label>
            <Textarea value={formData.key_features} onChange={update('key_features')} rows={4} />
          </div>
          <div className="space-y-2">
            <Label>Use Cases</Label>
            <Input value={formData.use_cases} onChange={update('use_cases')} placeholder="Fleets, Buses, Logistics" />
          </div>
          <div className="space-y-2">
            <Label>Datasheet URL</Label>
            <Input value={formData.datasheet_url} onChange={update('datasheet_url')} placeholder="Link to PDF datasheet" />
          </div>
          <FormActions onCancel={closeDialog} isEditing={!!editingItem} />
        </form>
      </AdminHeader>

      {loading ? <LoadingGrid count={2} cols="grid-cols-1 md:grid-cols-2" /> : items.length === 0 ? (
        <EmptyState Icon={HardDrive} title="No MDVR recorders yet" subtitle="Add your first MDVR recorder" onAdd={openCreate} addLabel="Add Recorder" />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {items.map(item => (
            <ItemCard key={item.id} item={item} titleField="model_name" descField="short_description" imageField="main_image"
              statusBadge={<span className="text-xs px-2 py-1 rounded bg-blue-100 text-blue-700">{item.channel_count || 'N/A'}</span>}
              Icon={HardDrive} onEdit={openEdit} onDelete={deleteItem} />
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminMDVR;
